import React, { Suspense, lazy, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface LazyLoaderProps {
  factory: () => Promise<{ default: React.ComponentType<any> }>;
  fallback?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  minLoadTime?: number; // Minimum time to show loading state (prevents flash)
}

interface LoadingSkeletonProps {
  type?: 'dashboard' | 'panel' | 'table' | 'form';
  className?: string;
}

// Skeleton components for different UI types
const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({ type = 'panel', className = '' }) => {
  const baseClasses = "animate-pulse bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%]";
  
  const skeletonVariants = {
    dashboard: (
      <div className={`space-y-6 p-6 ${className}`}>
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div className={`h-8 w-64 rounded-lg ${baseClasses}`} />
          <div className={`h-8 w-32 rounded-lg ${baseClasses}`} />
        </div>
        
        {/* Stats cards skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="p-4 rounded-xl border border-slate-200">
              <div className={`h-4 w-24 rounded ${baseClasses} mb-3`} />
              <div className={`h-8 w-16 rounded ${baseClasses} mb-2`} />
              <div className={`h-3 w-32 rounded ${baseClasses}`} />
            </div>
          ))}
        </div>
        
        {/* Chart skeleton */}
        <div className="p-4 rounded-xl border border-slate-200">
          <div className={`h-6 w-48 rounded ${baseClasses} mb-4`} />
          <div className={`h-64 w-full rounded ${baseClasses}`} />
        </div>
      </div>
    ),
    
    panel: (
      <div className={`space-y-4 p-4 ${className}`}>
        <div className={`h-6 w-48 rounded ${baseClasses}`} />
        <div className={`h-4 w-full rounded ${baseClasses}`} />
        <div className={`h-4 w-3/4 rounded ${baseClasses}`} />
        <div className={`h-32 w-full rounded ${baseClasses}`} />
      </div>
    ),
    
    table: (
      <div className={`space-y-3 p-4 ${className}`}>
        <div className={`h-6 w-32 rounded ${baseClasses} mb-4`} />
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="flex space-x-4">
            <div className={`h-4 w-1/4 rounded ${baseClasses}`} />
            <div className={`h-4 w-1/3 rounded ${baseClasses}`} />
            <div className={`h-4 w-1/4 rounded ${baseClasses}`} />
            <div className={`h-4 w-1/6 rounded ${baseClasses}`} />
          </div>
        ))}
      </div>
    ),
    
    form: (
      <div className={`space-y-4 p-4 ${className}`}>
        <div className={`h-6 w-32 rounded ${baseClasses} mb-6`} />
        {[1, 2, 3].map(i => (
          <div key={i} className="space-y-2">
            <div className={`h-4 w-24 rounded ${baseClasses}`} />
            <div className={`h-10 w-full rounded ${baseClasses}`} />
          </div>
        ))}
        <div className={`h-10 w-32 rounded ${baseClasses} mt-6`} />
      </div>
    )
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full"
    >
      {skeletonVariants[type]}
    </motion.div>
  );
};

// Enhanced error boundary for lazy loaded components
class LazyErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Lazy loading error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center justify-center p-8 text-center"
        >
          <div className="w-16 h-16 mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Component Failed to Load</h3>
          <p className="text-gray-600 mb-4">There was an error loading this component. Please try refreshing the page.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Refresh Page
          </button>
        </motion.div>
      );
    }

    return this.props.children;
  }
}

// Main lazy loader component
export const LazyLoader: React.FC<LazyLoaderProps> = ({
  factory,
  fallback,
  children,
  className = '',
  minLoadTime = 500
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [Component, setComponent] = useState<React.ComponentType<any> | null>(null);
  const [startTime] = useState(Date.now());

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let isMounted = true;

    const loadComponent = async () => {
      try {
        const module = await factory();
        
        if (!isMounted) return; // Component unmounted during loading
        
        const elapsed = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadTime - elapsed);
        
        // Ensure minimum loading time to prevent flash
        timeoutId = setTimeout(() => {
          if (isMounted) {
            setComponent(() => module.default);
            setIsLoading(false);
          }
        }, remainingTime);
      } catch (error) {
        console.error('Failed to load component:', error);
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadComponent();

    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [factory, minLoadTime, startTime]);

  if (isLoading) {
    return (
      <div className={className}>
        {fallback || <LoadingSkeleton type="panel" />}
      </div>
    );
  }

  if (!Component) {
    return (
      <LazyErrorBoundary>
        <div className="flex items-center justify-center p-8">
          <p className="text-gray-500">Failed to load component</p>
        </div>
      </LazyErrorBoundary>
    );
  }

  return (
    <LazyErrorBoundary>
      <AnimatePresence mode="wait">
        <motion.div
          key="loaded-component"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className={className}
        >
          <Component {...(children as Record<string, unknown>)} />
        </motion.div>
      </AnimatePresence>
    </LazyErrorBoundary>
  );
};

// Hook for creating lazy components with caching
export const useLazyComponent = (factory: () => Promise<{ default: React.ComponentType<any> }>) => {
  return React.useMemo(() => lazy(factory), [factory]);
};

// Pre-built skeleton components for common use cases
export const DashboardSkeleton = () => <LoadingSkeleton type="dashboard" />;
export const PanelSkeleton = () => <LoadingSkeleton type="panel" />;
export const TableSkeleton = () => <LoadingSkeleton type="table" />;
export const FormSkeleton = () => <LoadingSkeleton type="form" />;

export default LazyLoader;